import React, { useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useChartCumulativeSpend } from '../../../hooks/useChartData';
import ChartCard from '../wrappers/ChartCard';
import nivoCustomTheme from '../theme/nivoCustomTheme';
import useResponsive from '../../../hooks/useResponsive';

const TABLEAU10 = ['#4e79a7','#f28e2c','#e15759','#76b7b2','#59a14f','#edc949','#af7aa1','#ff9da7','#9c755f','#bab0ab'];

const formatCLP = (v) => `$${Math.round(v).toLocaleString('es-CL')}`;

function CustomLegend({ keys, colors }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', mt: 1.5, px: 0.5 }}>
      {keys.map((key, i) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors?.[i] ?? TABLEAU10[i % 10], flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>{key}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function BurnRateLineChart({ dateFrom, dateTo, categoryIds }) {
  const { isMobile } = useResponsive();
  // Shares the same Apollo query as CumulativeStackedBarChart — served from cache
  const { data, loading, error } = useChartCumulativeSpend({ dateFrom, dateTo, categoryIds });

  const { nivoData, keys, colors } = useMemo(() => {
    if (!data?.chartCumulativeSpend) return { nivoData: [], keys: [], colors: [] };
    const { keys, colors, rows } = data.chartCumulativeSpend;
    const nivoData = keys.map((cat) => ({
      id: cat,
      data: rows.map((row) => ({
        x: row.date,
        y: JSON.parse(row.jsonValues)[cat] ?? 0,
      })),
    }));
    return { nivoData, keys, colors };
  }, [data]);

  return (
    <ChartCard
      title="Burn Rate por Categoría"
      subtitle="Gasto acumulado diario por línea de categoría"
      loading={loading && !data}
      error={error}
      height={320}
        footer={<CustomLegend keys={keys} colors={colors} />}
    >
      <ResponsiveLine
        data={nivoData}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, stacked: false }}
        theme={nivoCustomTheme}
        colors={colors}
        margin={isMobile
          ? { top: 10, right: 10, bottom: 60, left: 50 }
          : { top: 10, right: 20, bottom: 60, left: 70 }
        }
        curve="monotoneX"
        lineWidth={2.5}
        pointSize={4}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        enableArea={false}
        useMesh={true}
        axisBottom={{
          tickSize: 0,
          tickPadding: 6,
          tickRotation: -45,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          format: (v) => `$${(v / 1000).toFixed(0)}k`,
        }}
        enableGridY={true}
        enableGridX={false}
        tooltip={({ point }) => (
          <div style={{ background: '#fff', padding: '8px 12px', borderRadius: 8, border: '1px solid #E8E5DF', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: point.serieColor }}>{point.serieId}</div>
            <div>{point.data.x}: {formatCLP(point.data.y)}</div>
          </div>
        )}
        legends={[]}
        animate={true}
        motionConfig="gentle"
      />
    </ChartCard>
  );
}
